import { useEffect, useState } from "react";
import { Clock, AlertTriangle } from "lucide-react";

/**
 * Live countdown timer down to seconds.
 * Re-renders every 1s. Shows "Expiré" when target reached.
 * Display compacts naturally: 89j 12h 34m 09s → 12h 34m 09s → 34m 09s → 09s
 */
export default function Countdown({ until, label = "Expire dans", compact = false, testid }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const target = new Date(until).getTime();
    if (isNaN(target)) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [until]);

  const target = new Date(until).getTime();
  if (isNaN(target)) return null;
  const diff = target - now;
  const expired = diff <= 0;

  const totalSec = Math.max(0, Math.floor(diff / 1000));
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  const pad = (n) => String(n).padStart(2, "0");

  const text = expired
    ? "Expiré"
    : days > 0
    ? `${days}j ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`
    : hours > 0
    ? `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`
    : `${pad(minutes)}m ${pad(seconds)}s`;

  const urgent = !expired && diff < 1000 * 60 * 60 * 24 * 7; // < 7 days

  const color = expired
    ? "bg-cream-100 text-plum-700/50 border-cream-200"
    : urgent
    ? "bg-pink-50 text-pink-600 border-pink-200"
    : "bg-mint-50 text-mint-600 border-mint-200";

  const Icon = expired ? AlertTriangle : Clock;

  if (compact) {
    return (
      <span
        data-testid={testid}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tabular-nums border ${color}`}
        aria-label={`${label} ${text}`}
      >
        <Icon size={11} strokeWidth={2.2} />
        {text}
      </span>
    );
  }

  return (
    <div
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border ${color}`}
    >
      <Icon size={14} strokeWidth={2.2} />
      <div className="leading-none">
        <div className="text-[9px] font-bold uppercase tracking-wider opacity-70">{label}</div>
        <div className="font-mono font-bold text-[14px] tabular-nums mt-0.5">{text}</div>
      </div>
    </div>
  );
}
